// Presentational shift-checkout summary. Given a computed `checkout` object from
// GET /pos/reports/checkout, renders the cash reconciliation, sales summary,
// comps by type, category breakdown, and voids. Used by the Register (self
// checkout) and POS Reports (manager view).
const money = (c) => (c == null ? '—' : '$' + (Number(c || 0) / 100).toFixed(2));
const signed = (c) => {
  if (c == null) return '—';
  const v = Number(c || 0);
  const s = '$' + (Math.abs(v) / 100).toFixed(2);
  return v < 0 ? `(${s})` : s;
};

const Row = ({ label, value, strong, tone }) => (
  <div className={`flex justify-between ${strong ? 'font-bold text-base' : 'text-sm'} ${tone === 'muted' ? 'text-gray-400' : ''}`}>
    <span>{label}</span>
    <span className={`tabular-nums ${tone === 'good' ? 'text-emerald-400' : tone === 'bad' ? 'text-red-400' : ''}`}>{value}</span>
  </div>
);

export default function PosCheckoutView({ checkout: c }) {
  if (!c) return null;
  const balanced = c.overShortCents == null ? null : c.overShortCents === 0;
  return (
    <div className="space-y-4 text-gray-100">
      {/* Bottom line */}
      <div className="bg-gray-900 rounded-xl p-4 space-y-1 border border-gray-700">
        <Row label="Cash due to house" value={money(c.cashToTurnInCents)} />
        <Row label="Cash turned in (counted)" value={money(c.countedCashCents)} />
        <Row label="Over / (Short)" value={signed(c.overShortCents)} strong tone={c.overShortCents == null ? undefined : c.overShortCents === 0 ? 'good' : 'bad'} />
        <div className="border-t border-gray-700 my-1" />
        <Row label="Chip-wallet charged" value={money(c.chipWalletCents)} tone="muted" />
        {balanced != null && (
          <div className={`mt-2 text-center text-sm font-bold rounded-lg py-1.5 ${balanced ? 'bg-emerald-900/50 text-emerald-300' : 'bg-red-900/50 text-red-300'}`}>
            {balanced ? 'BALANCED' : 'EXCEPTION — OVER/SHORT'}
          </div>
        )}
      </div>

      {/* Cash reconciliation */}
      <div>
        <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Cash reconciliation</h4>
        <div className="bg-gray-900 rounded-lg p-3 space-y-1">
          <Row label="Opening bank" value={money(c.openingBankCents)} tone="muted" />
          <Row label="Cash sales" value={money(c.cashSalesCents)} tone="muted" />
          <Row label="Cash refunds" value={money(c.cashRefundsCents)} tone="muted" />
          <Row label="Expected drawer" value={money(c.expectedCashCents)} />
          <Row label="Counted" value={money(c.countedCashCents)} />
          <Row label="Over / (Short)" value={signed(c.overShortCents)} tone={c.overShortCents == null ? undefined : c.overShortCents === 0 ? 'good' : 'bad'} />
        </div>
      </div>

      {/* Sales summary */}
      <div>
        <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Sales summary</h4>
        <div className="bg-gray-900 rounded-lg p-3 space-y-1">
          <Row label="Orders" value={c.orderCount} tone="muted" />
          <Row label="Gross" value={money(c.grossCents)} tone="muted" />
          <Row label="Tax" value={money(c.taxCents)} tone="muted" />
          <Row label="Comps / discounts" value={money(c.discountsCents)} tone="muted" />
          <Row label="Net" value={money(c.netCents)} strong />
        </div>
      </div>

      {/* Comps by type */}
      {c.compsByType && c.compsByType.length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Comps &amp; discounts</h4>
          <div className="bg-gray-900 rounded-lg p-3 space-y-1">
            {c.compsByType.map((r) => <Row key={r.type} label={r.label} value={money(r.cents)} tone="muted" />)}
          </div>
        </div>
      )}

      {/* Sales by category */}
      {c.salesByCategory && c.salesByCategory.length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Sales by category</h4>
          <div className="bg-gray-900 rounded-lg p-3 space-y-1">
            {c.salesByCategory.map((r) => <Row key={r.category} label={r.category} value={money(r.cents)} tone="muted" />)}
          </div>
        </div>
      )}

      {/* Voids & refunds */}
      {c.voids && c.voids.length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Voids &amp; refunds</h4>
          <div className="bg-gray-900 rounded-lg p-3 space-y-1">
            {c.voids.map((v, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span>#{v.orderNumber} <span className="text-gray-500">{v.reason || ''}</span></span>
                <span className="tabular-nums text-red-300">{money(v.refundedCents)} {v.tenders}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
